import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { firstValueFrom } from 'rxjs';

import { Store } from '@ngrx/store';

import { selectSelectedChannel } from '../../../../store/app.selectors';
import { MessagingEnvironmentService } from '../../../../core/services/messaging-environment.service';
import { SnackBarService } from '../../../../core/services/snackbar.service';
import { TranslaterService } from '../../../../modules/chat/services/translater.service';
import { LogActions, LogModules } from '../../services/logs.service';
import { TextProcessorService } from '../../services/text-processor.service';
import { AppStateWithChat } from '../../store/chat.reducer';

@Component({
  selector: 'ngx-text-processor',
  templateUrl: './text-processor.component.html',
  styleUrls: ['./text-processor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TextProcessorModalComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<TextProcessorModalComponent>);
  data = inject<{ original: string; isSmartResponse: boolean }>(MAT_DIALOG_DATA);
  private textProcessorService = inject(TextProcessorService);
  translaterService = inject(TranslaterService);
  private store = inject(Store<AppStateWithChat>);
  env = inject(MessagingEnvironmentService);
  private snackBarService = inject(SnackBarService);

  selectedChannel = toSignal(this.store.select(selectSelectedChannel), { initialValue: null });

  professionalized = '';
  translated = '';
  professionalizedTranslated = '';
  original = signal('');
  suggested = signal('');
  isLoading = signal(true);
  originalTab = signal(false);
  canProffesionalize = false;

  ngOnInit(): void {
    this.verifyCanProffesionalize();
    this.processText();
  }

  async processText(): Promise<void> {
    this.original.set(this.data.original);
    if (this.translaterService.verifyCanTranslateWithLocalSetting && this.canProffesionalize) {
      try {
        await this.translate();
        await this.professionalize();
        this.professionalizedTranslated = await firstValueFrom(
          this.translaterService.translateText(this.professionalized, this.selectedChannel()?.lang ?? '')
        );
        this.original.set(this.translated);
        this.suggested.set(this.professionalizedTranslated);
      } catch {
        this.snackBarService.showErrorSnackBar('Failed to professionalize and translate message, please try again');
      }
    } else {
      if (this.translaterService.verifyCanTranslateWithLocalSetting) {
        await this.translate();
      }
      if (this.canProffesionalize) {
        this.professionalize();
      }
    }

    this.isLoading.set(false);
  }

  async translate(): Promise<void> {
    try {
      this.translated = await firstValueFrom(
        this.translaterService.translateText(this.data.original, this.selectedChannel()?.lang ?? '')
      );
      this.suggested.set(this.translated);
    } catch {
      this.snackBarService.showErrorSnackBar('Failed to translate message, please try again');
    }
  }

  async professionalize(): Promise<void> {
    try {
      this.professionalized = await firstValueFrom(this.textProcessorService.professionalizeMessage(this.data.original));
      this.suggested.set(this.professionalized);
    } catch {
      this.snackBarService.showErrorSnackBar('Failed to professionalize message, please try again');
    }
  }

  close(): void {
    this.dialogRef.close(null);
  }

  sendResponse(text: string, suggested = false): void {
    let log: Record<string, unknown> | null = {};
    if (this.canProffesionalize) {
      if (suggested) {
        log = {
          module: LogModules.PROFESSIONALIZESUGGESTED,
          action: LogActions.CLICK,
          raw: { original: this.data.original },
        };
      } else {
        log = {
          module: LogModules.PROFESSIONALIZEORIGINAL,
          action: LogActions.CLICK,
          raw: { suggested: this.professionalized },
        };
      }
    } else {
      log = null;
    }
    if (text) {
      this.dialogRef.close({ text, log });
    }
  }

  selectTranslation(): void {
    this.suggested.set(this.professionalizedTranslated);
    this.original.set(this.translated);
    this.originalTab.set(false);
  }

  selectOriginal(): void {
    this.suggested.set(this.professionalized);
    this.original.set(this.data.original);
    this.originalTab.set(true);
  }

  verifyCanProffesionalize(): void {
    this.canProffesionalize = this.textProcessorService.verifyCanProffesionalize() && !this.data.isSmartResponse;
  }
}
